import { Spinner } from '@optools/components/loading/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { LitmusValidationSchema } from '@optools/services/operator-gateway/media-validation/types';
import { ValidateLitmusSchemaMutation } from '@optools/services/operator-gateway/types/generated/graphql';
import { CircleCheck, CircleHelp, CircleX } from 'lucide-react';
import { useMemo } from 'react';

export interface ValidationSuiteTableProps {
  loading: boolean;
  result: ValidateLitmusSchemaMutation['validate'];
  schema: LitmusValidationSchema | undefined;
}

export function ValidationSuiteTable({ loading, result, schema }: ValidationSuiteTableProps) {
  const resultMap = useMemo(() => {
    return (
      result?.ruleResults.reduce(
        (acc, result) => {
          if (result) acc[result.name] = result.result;

          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {}
    );
  }, [result]);

  return (
    <Table containerClassName="max-w-full">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="text-right">Result</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {schema?.rules.map((rule, index) => {
          const isSuccess = rule?.name ? resultMap[rule?.name] : undefined;
          const icon =
            typeof isSuccess === 'undefined' ? (
              <>
                <span className="sr-only" id="unknown-result">
                  unknown
                </span>
                <CircleHelp aria-labelledby="unknown-result" className="text-yellow-500" />
              </>
            ) : isSuccess ? (
              <>
                <span className="sr-only" id="success-result">
                  success
                </span>
                <CircleCheck aria-labelledby="success-result" className="text-green-700" />
              </>
            ) : (
              <>
                <span className="sr-only" id="failure-result">
                  failure
                </span>
                <CircleX aria-labelledby="failure-result" className="text-red-600" />
              </>
            );
          return (
            <TableRow key={rule?.name ?? index}>
              <TableCell>{rule?.name}</TableCell>
              <TableCell className="max-w-3xl whitespace-normal">{rule?.description}</TableCell>
              <TableCell>
                <div className="flex min-h-[24px] items-center justify-end">
                  {loading ? <Spinner /> : result ? icon : null}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
